import { Entity, UniqueEntityID } from '@domain/core'

import { isRecord, parseDateOrFallback, toNonEmptyTrimmedString } from '@/utils/safe'

export type ChecklistProcedureType = 'normal' | 'abnormal' | 'emergency'
export type ChecklistItemType = 'check' | 'note' | 'divider'

export interface ChecklistBaseItem {
  id: string
  type: ChecklistItemType
  title: string
}

export interface ChecklistCheckItem extends ChecklistBaseItem {
  type: 'check'
  action: string
  description?: string
}

export interface ChecklistNoteItem extends ChecklistBaseItem {
  type: 'note'
  description: string
}

export interface ChecklistDividerItem extends ChecklistBaseItem {
  type: 'divider'
}

export type ChecklistItem = ChecklistCheckItem | ChecklistNoteItem | ChecklistDividerItem

export interface ChecklistProcedure {
  id: string
  name: string
  type: ChecklistProcedureType
  items: ChecklistItem[]
}

export interface ChecklistProps {
  userAircraftId: string
  name: string
  procedures: ChecklistProcedure[]
  createdAt: Date
  updatedAt: Date
}

export interface ChecklistPojo {
  id: string
  userAircraftId: string
  name: string
  procedures: string
  createdAt: Date
  updatedAt: Date
}

export const CHECKLIST_PROCEDURE_TYPES: ChecklistProcedureType[] = ['normal', 'abnormal', 'emergency']

export const cloneChecklistProcedures = (procedures: ChecklistProcedure[]): ChecklistProcedure[] => {
  return procedures.map((procedure) => ({
    id: new UniqueEntityID().toString(),
    name: procedure.name,
    type: procedure.type,
    items: procedure.items.map((item) => ({ ...item, id: new UniqueEntityID().toString() }))
  }))
}

export const isChecklistProcedureType = (value: unknown): value is ChecklistProcedureType => {
  return CHECKLIST_PROCEDURE_TYPES.includes(value as ChecklistProcedureType)
}

const parseChecklistItem = (item: unknown, procedureId: string, itemIndex: number): ChecklistItem | null => {
  if (!isRecord(item)) return null

  const id = toNonEmptyTrimmedString(item.id) ?? `${procedureId}:item:${itemIndex}`
  const title = toNonEmptyTrimmedString(item.title)
  const type = item.type

  if (typeof type !== 'string') return null

  if (type === 'divider') {
    if (!title) return null

    return { id, title, type: 'divider' }
  }

  if (type === 'check') {
    if (!title) return null

    const action = toNonEmptyTrimmedString(item.action)
    if (!action) return null

    const description = typeof item.description === 'string' ? item.description : undefined

    return {
      id,
      title,
      type: 'check',
      action,
      description
    }
  }

  if (type === 'note') {
    if (typeof item.description !== 'string') return null

    return {
      id,
      title: title ?? '',
      type: 'note',
      description: item.description
    }
  }

  return null
}

export const parseChecklistProcedures = (procedures: unknown): ChecklistProcedure[] => {
  if (typeof procedures !== 'string') return []

  let parsedProcedures: unknown
  try {
    parsedProcedures = JSON.parse(procedures)
  } catch (error) {
    return []
  }

  if (!Array.isArray(parsedProcedures)) return []

  return parsedProcedures.reduce<ChecklistProcedure[]>((acc, procedure, procedureIndex) => {
    if (!isRecord(procedure)) return acc

    const id = toNonEmptyTrimmedString(procedure.id) ?? `procedure:${procedureIndex}`
    const name = toNonEmptyTrimmedString(procedure.name)
    const type = procedure.type
    const items = Array.isArray(procedure.items) ? procedure.items : null

    if (!name || !items || typeof type !== 'string') return acc
    if (!isChecklistProcedureType(type)) return acc

    acc.push({
      id,
      name,
      type,
      items: items.map((item, itemIndex) => parseChecklistItem(item, id, itemIndex)).filter(Boolean) as ChecklistItem[]
    })

    return acc
  }, [])
}

export class Checklist extends Entity<ChecklistProps, ChecklistPojo> {
  private constructor(props: ChecklistProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: ChecklistProps, id?: UniqueEntityID): Checklist {
    if (!props.name?.trim()) {
      throw new Error('Checklist name cannot be empty')
    }

    if (!props.userAircraftId?.trim()) {
      throw new Error('Checklist userAircraftId cannot be empty')
    }

    return new Checklist(props, id)
  }

  public get userAircraftId(): string {
    return this.props.userAircraftId
  }

  public set userAircraftId(userAircraftId: string) {
    if (!userAircraftId?.trim()) {
      throw new Error('Checklist userAircraftId cannot be empty')
    }

    this.props.userAircraftId = userAircraftId
  }

  public get name(): string {
    return this.props.name
  }

  public set name(name: string) {
    if (!name?.trim()) {
      throw new Error('Checklist name cannot be empty')
    }

    this.props.name = name
  }

  public get procedures(): ChecklistProcedure[] {
    return this.props.procedures
  }

  public set procedures(procedures: ChecklistProcedure[]) {
    this.props.procedures = procedures
  }

  public get createdAt(): Date {
    return this.props.createdAt
  }

  public set createdAt(createdAt: Date) {
    this.props.createdAt = createdAt
  }

  public get updatedAt(): Date {
    return this.props.updatedAt
  }

  public set updatedAt(updatedAt: Date) {
    this.props.updatedAt = updatedAt
  }

  public toPojo(): ChecklistPojo {
    return {
      id: this.id.toString(),
      userAircraftId: this.userAircraftId,
      name: this.name,
      procedures: JSON.stringify(this.procedures),
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    }
  }

  public static fromPojo(pojo: ChecklistPojo): Checklist {
    const fallbackDate = new Date()
    const pojoRecord: Record<string, unknown> = isRecord(pojo) ? pojo : {}

    const checklistProps: ChecklistProps = {
      userAircraftId: toNonEmptyTrimmedString(pojoRecord.userAircraftId) ?? 'unknown-aircraft',
      name: toNonEmptyTrimmedString(pojoRecord.name) ?? 'Checklist',
      procedures: parseChecklistProcedures(pojoRecord.procedures),
      createdAt: parseDateOrFallback(pojoRecord.createdAt, fallbackDate),
      updatedAt: parseDateOrFallback(pojoRecord.updatedAt, fallbackDate)
    }

    return new Checklist(checklistProps, new UniqueEntityID(toNonEmptyTrimmedString(pojoRecord.id) ?? undefined))
  }
}
